/* First campaign in HTML; subsequent images load on intent or just before autoplay. */
if (!customElements.get('vs-offer-slider')) {
  customElements.define('vs-offer-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.buttons = Array.from(this.querySelectorAll('[data-go]'));
      this.pauseButton = this.querySelector('[data-pause]');
      this.status = this.querySelector('[data-status]');
      this.viewport = this.querySelector('.vs-offers__viewport');
      this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.inView = true;
      this.focused = false;
      this.hovered = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches || this.dataset.editor === 'true';
      this.interval = Math.max(5000, Math.min(15000, Number(this.dataset.interval) || 10000));
      this.querySelectorAll('.vs-offers__controls button').forEach(button => { button.disabled = false; });
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.index - 1, true), options);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.index + 1, true), options);
      this.buttons.forEach(button => button.addEventListener('click', () => this.show(Number(button.dataset.go), true), options));
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePause();
        this.schedule();
      }, options);
      // A navigation hover/focus is a useful signal; don't download other campaigns at startup.
      const prepareControl = event => {
        const button = event.target.closest('[data-go],[data-prev],[data-next]');
        if (!button) return;
        const index = button.hasAttribute('data-go') ? Number(button.dataset.go) : this.index + (button.hasAttribute('data-prev') ? -1 : 1);
        this.prepare(index);
      };
      this.querySelector('.vs-offers__controls')?.addEventListener('pointerover', prepareControl, options);
      this.querySelector('.vs-offers__controls')?.addEventListener('focusin', prepareControl, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); }
      }, options);
      // Arrow keys apply to the controls, never steal keys from links or form fields.
      this.querySelector('.vs-offers__controls')?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        this.buttons[this.index]?.focus();
      }, options);
      this.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) return;
        this.touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { ...options, passive: true });
      this.addEventListener('touchend', event => {
        if (!this.touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1), true);
      }, { ...options, passive: true });
      this.addEventListener('touchcancel', () => { this.touchStart = null; }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) { this.paused = true; this.updatePause(); }
        this.schedule();
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }
      if (this.dataset.editor === 'true') {
        // Materialize editor blocks so Shopify can select them; keep their images lazy.
        this.slides.forEach(slide => this.materialize(slide, true));
        document.addEventListener('shopify:block:select', event => {
          const slide = event.target.closest('[data-slide]');
          const index = this.slides.indexOf(slide);
          if (index !== -1) this.show(index, true);
        }, options);
      }
      const selectHash = event => {
        const kind = { '#e-bike-sale': 'bikes', '#zubehoer-bestseller': 'bestsellers', '#pausen-set': 'pausen' }[location.hash];
        const index = this.slides.findIndex(slide => slide.dataset.slide === kind);
        if (index !== -1) {
          this.show(index, true);
          if (event?.type === 'hashchange') this.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      };
      window.addEventListener('hashchange', selectHash, options);
      this.updatePause();
      selectHash();
      this.schedule();
    }
    materialize(slide, lazy = false) {
      // Layout already includes every campaign. Restore only this slide's media
      // attributes, srcset before src, so the browser can pick one fitting image.
      slide.querySelectorAll('img').forEach(image => { if (lazy) image.loading = 'lazy'; });
      slide.querySelectorAll('[data-vs-srcset]').forEach(image => {
        image.setAttribute('srcset', image.getAttribute('data-vs-srcset'));
        image.removeAttribute('data-vs-srcset');
      });
      slide.querySelectorAll('[data-vs-src]').forEach(image => {
        image.setAttribute('src', image.getAttribute('data-vs-src'));
        image.removeAttribute('data-vs-src');
      });
    }
    prepare(index) {
      if (!this.slides.length) return Promise.resolve();
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      this.materialize(slide);
      return Promise.all(Array.from(slide.querySelectorAll('img'), image => {
        // The next hidden slide is intentionally fetched just before it is needed.
        image.loading = 'eager';
        return image.decode ? image.decode().catch(() => {}) : Promise.resolve();
      }));
    }
    show(index, userInitiated = false) {
      if (!this.slides.length) return;
      const next = (index + this.slides.length) % this.slides.length;
      if (userInitiated) this.paused = true;
      this.materialize(this.slides[next]);
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        slide.inert = i !== next;
        slide.setAttribute('aria-hidden', String(i !== next));
      });
      this.index = next;
      this.buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === next)));
      if (userInitiated && this.status) this.status.textContent = `${this.slides[next].dataset.label} (${next + 1}/${this.slides.length})`;
      this.updatePause();
      this.schedule();
    }
    updatePause() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
    }
    schedule() {
      clearTimeout(this.timer);
      clearTimeout(this.prefetchTimer);
      this.prepared = null;
      const scheduleId = this.scheduleId = (this.scheduleId || 0) + 1;
      if (this.slides.length > 1 && !this.paused && !this.hovered && !this.focused && this.inView && !document.hidden && !this.reducedMotion.matches) {
        const next = this.index + 1;
        this.prefetchTimer = setTimeout(() => { this.prepared = this.prepare(next); }, this.interval - 1500);
        this.timer = setTimeout(async () => {
          await (this.prepared || this.prepare(next));
          if (scheduleId !== this.scheduleId || !this.isConnected) return;
          this.prepared = null;
          this.show(next);
        }, this.interval);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.prefetchTimer);
      this.scheduleId = (this.scheduleId || 0) + 1;
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
